/**
 * Centralised, typed access to Vite-injected env vars.
 *
 * Reads are non-throwing so the app boots without a configured .env
 * (the landing page should still render). Call `requireEnv` from code
 * paths that genuinely need a value (e.g. the Supabase client factory).
 */

export const env = {
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ?? "",
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ?? "",
  API_BASE_URL: import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000",
  /** Where Supabase redirects after magic-link / OAuth. Must match
   *  the "Site URL" in Supabase Dashboard → Auth → Settings.
   *  Default matches Vite's dev port (5173). Override in frontend/.env
   *  for production (e.g. https://batch-cooking.onrender.com). */
  AUTH_REDIRECT_URL:
    import.meta.env.VITE_AUTH_REDIRECT_URL ?? "http://localhost:5173",
  /** Supabase Storage bucket for photo assets. Empty = use Unsplash CDN. */
  SUPABASE_STORAGE_PHOTOS_BUCKET:
    import.meta.env.VITE_SUPABASE_STORAGE_PHOTOS_BUCKET ?? "",
  /** VAPID public key for Web Push notifications (RFC 8292).
   *  Generated with: npx web-push generate-vapid-keys.
   *  Empty = push notifications disabled. */
  VAPID_PUBLIC_KEY: import.meta.env.VITE_VAPID_PUBLIC_KEY ?? "",
  /** RevenueCat public SDK keys (NOT the secret key), one per platform —
   *  RevenueCat → Project settings → Apps → <app> → Public API key.
   *  Web Billing keys start with `rcb_`, App Store with `appl_`, Play Store
   *  with `goog_`. A store key only works for its own platform, so the key is
   *  picked at runtime by `getRevenueCatApiKey()` from Capacitor.getPlatform().
   *  Empty = purchases unavailable on that platform (paywall says so). */
  REVENUECAT_WEB_KEY:
    // `||`, not `??`: a blank VITE_REVENUECAT_WEB_KEY (as in .env.example or
    // an unset Render var) must still fall back to the legacy single key.
    import.meta.env.VITE_REVENUECAT_WEB_KEY ||
    import.meta.env.VITE_REVENUECAT_API_KEY ||
    "",
  REVENUECAT_IOS_KEY: import.meta.env.VITE_REVENUECAT_IOS_KEY ?? "",
  REVENUECAT_ANDROID_KEY: import.meta.env.VITE_REVENUECAT_ANDROID_KEY ?? "",
  /** RevenueCat product identifier for the premium subscription.
   *  Must match a product created in App Store Connect / Google Play Console
   *  and linked in RevenueCat dashboard → Products. */
  REVENUECAT_PRODUCT_ID: import.meta.env.VITE_REVENUECAT_PRODUCT_ID ?? "",
  /** Google Analytics 4 Measurement ID (format: G-XXXXXXXXXX).
   *  Empty = analytics disabled. Web-only; skipped in native Capacitor builds. */
  GA_MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID ?? "",
  /** Google OAuth **Web** client ID (xxx.apps.googleusercontent.com).
   *  Used as the ID-token audience for native Google Sign-In on Android and
   *  as `serverClientId` on iOS. Empty = native Google button hidden.
   *  See docs/native-auth-setup.md. */
  GOOGLE_WEB_CLIENT_ID: import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID ?? "",
  /** Google OAuth **iOS** client ID. Empty = Google button hidden on iOS. */
  GOOGLE_IOS_CLIENT_ID: import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID ?? "",
} as const;

export function requireEnv<K extends keyof typeof env>(key: K): string {
  const value = env[key];
  if (!value) {
    throw new Error(
      `Missing env var VITE_${key}. Copy frontend/.env.example to frontend/.env and fill it in.`,
    );
  }
  return value;
}

/** RevenueCat public SDK key for `platform` (Capacitor.getPlatform()). */
export function getRevenueCatApiKey(platform: string): string {
  if (platform === "ios") return env.REVENUECAT_IOS_KEY;
  if (platform === "android") return env.REVENUECAT_ANDROID_KEY;
  return env.REVENUECAT_WEB_KEY;
}

export function isSupabaseConfigured(): boolean {
  return env.SUPABASE_URL.length > 0 && env.SUPABASE_ANON_KEY.length > 0;
}
